// node_modules
import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useMobile } from "../../../hooks/useMobile";

// redux
import {
  useDeleteStationMutation,
  useGetStationListQuery,
  useImportStationMutation,
  useLazyGetDeletedStationListQuery,
  useLazyGetStationDataQuery,
  usePermanentlyDeleteStationMutation,
  useRecoverStationMutation,
  useUpdateSoundActiveMutation,
  useUpdateStationMutation,
  useUploadImageMutation,
  useUploadSoundMutation,
} from "../../../redux/services/foodStation";
import { setLoading } from "../../../redux/slices/app.slice";

// primereact components
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { FileUpload } from "primereact/fileupload";

// context
import { ToastContext } from "../../../App";
import {
  exportStationToCsv,
  parseStationCSVToJson,
} from "../../../utils/csvHelper";

// utils
import getSubdomain from "../../../utils/getSubdomain";
import { getImageUrl } from "../../../utils/getImageUrl";
import { getSoundUrl } from "../../../utils/getSoundUrl";

// styles
import * as styles from "./Stations.module.css";
import { classNames } from "primereact/utils";

import { ImportModal } from "./../../../components/ImportModal/ImportModal";
import { ImportButton } from "../../../components/ImportButton/ImportButton";

import { useImportModal } from "../../../context/ImportModalContext";

const Stations = () => {
  const showToast = useContext(ToastContext);
  const navigate = useNavigate();
  const { isMobile } = useMobile(1024);

  const dispatch = useDispatch();

  const {
    data: stations,
    isSuccess: isGetStationsSuccess,
    isError: isGetStationsError,
  } = useGetStationListQuery(getSubdomain());

  const [
    getDeletedStations,
    {
      data: deletedStations,
      isSuccess: isGetDeletedStationsSuccess,
      isError: isGetDeletedStationsError,
    },
  ] = useLazyGetDeletedStationListQuery(getSubdomain());

  const [
    recoverStation,
    { isSuccess: isRecoverStationSuccess, isError: isRecoverStationError },
  ] = useRecoverStationMutation();

  const [
    permanentlyDelete,
    {
      isSuccess: isPermanentlyDeleteSuccess,
      isError: isPermanentlyDeleteError,
    },
  ] = usePermanentlyDeleteStationMutation();

  const [updateStation] = useUpdateStationMutation();

  const [
    updateSoundActive,
    {
      isSuccess: isUpdateSoundActiveSuccess,
      isError: isUpdateSoundActiveError,
      error: updateSoundActiveError,
    },
  ] = useUpdateSoundActiveMutation();

  const [
    deleteStation,
    {
      isSuccess: isDeleteStationSuccess,
      isError: isDeleteStationError,
      error: deleteStationError,
    },
  ] = useDeleteStationMutation();

  const [
    uploadImage,
    {
      isSuccess: isUploadImageSuccess,
      isError: isUploadImageError,
      error: uploadImageError,
    },
  ] = useUploadImageMutation();

  const [
    uploadSound,
    {
      isSuccess: isUploadSoundSuccess,
      isError: isUploadSoundError,
      error: uploadSoundError,
    },
  ] = useUploadSoundMutation();

  const [getStationData] = useLazyGetStationDataQuery();

  const [
    importStation,
    {
      isSuccess: isImportStationSuccess,
      isError: isImportStationError,
      error: importStationError,
    },
  ] = useImportStationMutation();

  const { showImportModal, setShowImportModal } = useImportModal();

  const uploadRef = useRef(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [showSoundModal, setShowSoundModal] = useState(false);
  const [deleteMode, setDeleteMode] = useState("delete");
  const [id, setId] = useState(null);
  const [name, setName] = useState(null);
  const [imageStationName, setImageStationName] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [currentSound, setCurrentSound] = useState(null);
  const [data, setData] = useState([]);

  const goCreate = () => navigate("create");
  const goEdit = (rowData) => navigate(`${rowData.id}/edit`);

  useEffect(() => {
    if (isUpdateSoundActiveSuccess) {
      showToast("success", "Station", "Station sound active update");
    }
  }, [isUpdateSoundActiveSuccess]);

  useEffect(() => {
    if (isUploadImageSuccess) {
      setShowImageModal(false);
      showToast("success", "Station", "Image uploaded!");
    }
  }, [isUploadImageSuccess]);

  useEffect(() => {
    if (isUploadSoundSuccess) {
      setShowSoundModal(false);
      showToast("success", "Station", "Sound uploaded!");
    }
  }, [isUploadSoundSuccess]);

  useEffect(() => {
    if (isGetStationsError)
      showToast("error", "Station", "Unable to load stations");
  }, [isGetStationsError]);

  useEffect(() => {
    if (isGetDeletedStationsError)
      showToast("error", "Station", "Unable to load deleted stations");
  }, [isGetDeletedStationsError]);

  useEffect(() => {
    if (isRecoverStationError)
      showToast("error", "Station", "Unable to recover station");
  }, [isRecoverStationError]);

  useEffect(() => {
    if (isUpdateSoundActiveError) {
      showToast(
        "error",
        updateSoundActiveError?.status,
        updateSoundActiveError?.data?.message ||
          (updateSoundActiveError?.data?.errors && "Incorrect data"),
      );
    }
  }, [isUpdateSoundActiveError]);

  useEffect(() => {
    if (isDeleteStationSuccess) {
      showToast("success", "Station", "Station deleted!");
      setShowDeleteModal(false);
      getDeletedStations(getSubdomain());
    }
  }, [isDeleteStationSuccess]);

  useEffect(() => {
    if (isDeleteStationError)
      showToast(
        "error",
        deleteStationError?.status,
        deleteStationError?.data?.message,
      );
  }, [isDeleteStationError]);

  useEffect(() => {
    if (isUploadImageError)
      showToast(
        "error",
        uploadImageError?.status,
        uploadImageError?.data?.message ||
          (uploadImageError?.data?.errors && "Incorrect data"),
      );
  }, [isUploadImageError]);

  useEffect(() => {
    if (isUploadSoundError)
      showToast(
        "error",
        uploadSoundError?.status,
        uploadSoundError?.data?.message ||
          (uploadSoundError?.data?.errors && "Incorrect data"),
      );
  }, [isUploadSoundError]);

  useEffect(() => {
    if (isImportStationSuccess) {
      showToast("success", "Station", "Station imported!");
      uploadRef.current.clear();
      setShowImportModal(false);
    }
  }, [isImportStationSuccess]);

  useEffect(() => {
    if (isImportStationError) {
      showToast(
        "error",
        importStationError?.status,
        importStationError?.data?.message ||
          (importStationError?.data?.errors && "Incorrect data"),
      );
      uploadRef.current.clear();
    }
  }, [isImportStationError]);

  useEffect(() => {
    if (isGetStationsSuccess) {
      dispatch(setLoading(false));
      setData((prev) => [...prev, ...stations]);
      getDeletedStations(getSubdomain());
    }
  }, [isGetStationsSuccess]);

  useEffect(() => {
    let tempData = [];
    if (isGetStationsSuccess) {
      tempData = stations;
    }
    if (isGetDeletedStationsSuccess) {
      tempData = [...tempData, ...deletedStations];
    }
    setData(tempData);
  }, [
    isGetStationsSuccess,
    stations,
    isGetDeletedStationsSuccess,
    deletedStations,
  ]);

  useEffect(() => {
    if (isGetStationsError) {
      dispatch(setLoading(false));
    }
  }, [isGetStationsError]);

  useEffect(() => {
    if (isRecoverStationSuccess) {
      getDeletedStations(getSubdomain());
    }
  }, [isRecoverStationSuccess]);

  useEffect(() => {
    if (isPermanentlyDeleteError) {
      showToast("error", "Station", "Unable to permanently delete station");
    }
  }, [isPermanentlyDeleteError]);

  useEffect(() => {
    if (isPermanentlyDeleteSuccess) {
      getDeletedStations(getSubdomain());
      setShowDeleteModal(false);
    }
  }, [isPermanentlyDeleteSuccess]);

  const onFileUpload = async (e) => {
    try {
      const file = e.files[0];
      const text = await file.text();
      const json = {
        ...parseStationCSVToJson(text),
        subdomain: getSubdomain(),
      };
      importStation(json);
    } catch (e) {
      console.error(e);
    }
  };

  const downloadCSVFile = (content, filename) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToCSV = async (rowData) => {
    try {
      const data = await getStationData(
        { id: rowData.id, subdomain: getSubdomain() },
        false,
      ).unwrap();
      console.log("Export data: ", data);

      const csvContent = exportStationToCsv(data);
      downloadCSVFile(csvContent, `${rowData.name}_export.csv`);
    } catch (error) {
      showToast("error", "Food Station", "Unable to export data");
    }
  };

  const openDeleteModal = (rowData, mode) => () => {
    setDeleteMode(mode);
    setId(rowData.id);
    setName(rowData.name);
    setShowDeleteModal(true);
  };

  const onDelete = () => {
    deleteStation({ id, subdomain: getSubdomain() });
  };

  const onPermanentlyDelete = () => {
    permanentlyDelete({ id, subdomain: getSubdomain() });
  };

  const openImageModal = (rowData) => () => {
    if (rowData?.isDeleted === 1) {
      return;
    }

    setCurrentImage(getImageUrl(rowData.image_name));
    setImageStationName(rowData.name);
    setId(rowData.id);
    setShowImageModal(true);
  };

  const openSoundModal = (rowData) => () => {
    setCurrentSound(getSoundUrl(rowData.sound_name));
    setId(rowData.id);
    setShowSoundModal(true);
  };

  const onImageSelect = (e) => {
    setCurrentImage(e.files[0].objectURL);
  };

  const onImageUpload = (e) => {
    uploadImage({
      id,
      subdomain: getSubdomain(),
      data: e.files[0],
      imageType: "station",
    });
  };

  const onSoundSelect = (e) => {
    setCurrentSound(e.files[0].objectURL);
  };

  const onSoundUpload = (e) => {
    uploadSound({ id, subdomain: getSubdomain(), data: e.files[0] });
  };

  const actionBodyTemplate = (rowData) => {
    if (rowData?.isDeleted === 1) {
      return (
        <div className={styles.cellSoundWrapper}>
          <Button
            icon="pi pi-upload"
            className={`p-button-text ${styles.exportBtn}`}
            onClick={() => exportToCSV(rowData)}
          />
          <Button
            icon="pi pi-refresh"
            className={`p-button-text ${styles.soundBtn}`}
            onClick={() =>
              recoverStation({ subdomain: getSubdomain(), id: rowData.id })
            }
          />
          <Button
            icon="pi pi-trash"
            className={`p-button-text ${styles.deleteBtn}`}
            onClick={openDeleteModal(rowData, "permanently")}
          />
        </div>
      );
    }

    return (
      <div className={styles.cellSoundWrapper}>
        <Button
          icon="pi pi-upload"
          className={`p-button-text ${styles.exportBtn}`}
          onClick={() => exportToCSV(rowData)}
        />
        <Button
          icon="pi pi-pencil"
          className={`p-button-text ${styles.soundBtn}`}
          onClick={() => goEdit(rowData)}
        />
        <Button
          icon="pi pi-trash"
          className={`p-button-text ${styles.deleteBtn}`}
          onClick={openDeleteModal(rowData, "soft")}
        />
      </div>
    );
  };

  return (
    <div className={styles.pageWrapper}>
      <div
        className={`flex justify-content-between align-items-center ${styles.header}`}
      >
        <div className={styles.headerContentContainer}>
          <h2>Stations</h2>
          <ImportButton onClick={() => setShowImportModal(true)} />
        </div>
        <Button className={styles.createBtn} onClick={goCreate}>
          <span>ADD STATION</span> <i className="pi pi-plus" />
        </Button>
      </div>
      {stations &&
        (isMobile ? (
          <div className={styles.cardList}>
            {data.map((station) => {
              const timeArray = station.order_restriction.split(":");

              return (
                <div
                  key={station.id}
                  className={`${styles.cardItem} ${
                    station?.isDeleted === 1 ? `${styles.disabled}` : ""
                  }`}
                >
                  {!!station.is_multi && (station.sub_stations?.length ?? 0) > 1 && (
                    <span
                      className={styles.multiBadge}
                      title={`${station.sub_stations.length} sub-stations`}
                    >
                      x{station.sub_stations.length}
                    </span>
                  )}
                  <div className={styles.cardInfo}>
                    <div
                      style={{
                        backgroundImage: `url(${getImageUrl(
                          station.image_name,
                        )})`,
                      }}
                      className={styles.cardImage}
                      onClick={openImageModal(station)}
                    >
                      <div>
                        <i className="pi pi-pencil"></i>
                      </div>
                    </div>
                    <div className={styles.info}>
                      <div className={styles.name}>{station.name}</div>
                      <div className={styles.subfield}>
                        {timeArray[1] === "00"
                          ? `Not restricted`
                          : `${timeArray[1]} minutes`}
                      </div>
                      {(station.notify_managers?.length ?? 0) > 0 && (
                        <div
                          className={styles.subfield}
                          title={station.notify_managers
                            .map((m) => m.name)
                            .join(", ")}
                        >
                          <i
                            className="pi pi-bell"
                            style={{ fontSize: 10, marginRight: 4 }}
                          />
                          {station.notify_managers.map((m) => m.name).join(", ")}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className={styles.actions}>
                    {station.active ? (
                      <button
                        className={classNames(
                          styles.cardEditBtn,
                          styles.cardInactiveBtn,
                        )}
                        disabled={station.isDeleted === 1}
                        onClick={() => {
                          updateStation({
                            id: station.id,
                            subdomain: getSubdomain(),
                            data: {
                              active: !station.active,
                            },
                          });
                        }}
                      >
                        <i className="pi pi-eye" />
                      </button>
                    ) : (
                      <button
                        className={classNames(
                          styles.cardEditBtn,
                          styles.cardActiveBtn,
                        )}
                        disabled={station.isDeleted === 1}
                        onClick={() => {
                          updateStation({
                            id: station.id,
                            subdomain: getSubdomain(),
                            data: {
                              active: !station.active,
                            },
                          });
                        }}
                      >
                        <i className="pi pi-eye-slash" />
                      </button>
                    )}
                    <Button
                      icon="pi pi-upload"
                      className={`p-button-text ${styles.exportBtnMobile}`}
                      onClick={() => exportToCSV(station)}
                    />
                    {station?.isDeleted === 1 ? (
                      <button
                        className={styles.cardEditBtn}
                        onClick={() =>
                          recoverStation({
                            subdomain: getSubdomain(),
                            id: station.id,
                          })
                        }
                      >
                        <i className="pi pi-refresh" />
                      </button>
                    ) : (
                      <button
                        className={styles.cardEditBtn}
                        onClick={() => goEdit(station)}
                      >
                        <i className="pi pi-pencil" />
                      </button>
                    )}
                    <button
                      className={styles.cardDeleteBtn}
                      onClick={openDeleteModal(
                        station,
                        station?.isDeleted === 1 ? "permanently" : "delete",
                      )}
                    >
                      <i className="pi pi-trash" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <DataTable value={data} rows={6}>
            <Column
              className={styles.hoverableCell}
              field="name"
              header="Name"
              body={(rowData) => {
                const subCount = rowData.sub_stations?.length ?? 0;
                return (
                  <div
                    className={classNames(
                      styles.cellInsider,
                      styles.columnNameDataCell,
                    )}
                  >
                    {!!rowData.is_multi && subCount > 1 && (
                      <span
                        className={styles.multiBadge}
                        title={`${subCount} sub-stations`}
                      >
                        x{subCount}
                      </span>
                    )}
                    {rowData.name}
                  </div>
                );
              }}
            />

            <Column
              className={styles.hoverableCell}
              body={(rowData) => {
                return (
                  <div
                    className={styles.cellImageWrapper}
                    onClick={openImageModal(rowData)}
                  >
                    <div
                      style={{
                        backgroundImage: `url(${getImageUrl(
                          rowData.image_name,
                        )})`,
                      }}
                      className={styles.imageCell}
                      onClick={openImageModal(rowData)}
                    >
                      <div>
                        <i className="pi pi-pencil"></i>
                      </div>
                    </div>
                  </div>
                );
              }}
              header="Image"
              style={{ width: "180px" }}
            />

            <Column
              className={styles.hoverableCell}
              body={(rowData) => {
                return (
                  <div className={styles.cellSoundWrapper}>
                    <Button
                      icon="pi pi-play"
                      className={`p-button-text ${styles.playBtn}`}
                      disabled={!rowData.sound_name || rowData.isDeleted === 1}
                      onClick={() => {
                        const audio = document.getElementById(
                          rowData.sound_name,
                        );
                        audio.play();
                      }}
                    />
                    {rowData.sound_name && (
                      <audio
                        id={rowData.sound_name}
                        style={{ visibility: "hidden", position: "absolute" }}
                        controls={true}
                        src={getSoundUrl(rowData.sound_name)}
                      />
                    )}
                    {rowData.sound_active ? (
                      <Button
                        icon="pi pi-eye"
                        className={`p-button-text ${styles.activeSoundBtn}`}
                        disabled={rowData.isDeleted === 1}
                        onClick={() => {
                          updateSoundActive({
                            id: rowData.id,
                            subdomain: getSubdomain(),
                            data: {
                              sound_active: !rowData.sound_active,
                            },
                          });
                        }}
                      />
                    ) : (
                      <Button
                        icon="pi pi-eye-slash"
                        className={`p-button-text ${styles.activeSoundBtn}`}
                        disabled={rowData.isDeleted === 1}
                        onClick={() => {
                          updateSoundActive({
                            id: rowData.id,
                            subdomain: getSubdomain(),
                            data: {
                              sound_active: !rowData.sound_active,
                            },
                          });
                        }}
                      />
                    )}

                    <Button
                      icon="pi pi-pencil"
                      className={`p-button-text ${styles.soundBtn}`}
                      onClick={openSoundModal(rowData)}
                      disabled={rowData.isDeleted === 1}
                    />
                  </div>
                );
              }}
              header="Sound"
              style={{ width: "242px" }}
            />
            <Column
              className={styles.hoverableCell}
              body={(rowData) => {
                const timeArray = rowData.order_restriction.split(":");

                return (
                  <div
                    className={classNames(
                      styles.cellInsider,
                      styles.columnRestrictionDataCell,
                    )}
                  >
                    {timeArray[1] === "00"
                      ? `Not restricted`
                      : `${timeArray[1]} minutes`}
                  </div>
                );
              }}
              field="order_restriction"
              header="Order Frequency"
              style={{ width: "184px" }}
            />
            <Column
              className={styles.hoverableCell}
              body={(rowData) => {
                const names = (rowData.notify_managers || []).map(
                  (m) => m.name,
                );
                return (
                  <div
                    className={classNames(styles.cellInsider)}
                    title={names.join(", ")}
                  >
                    {names.length ? names.join(", ") : "—"}
                  </div>
                );
              }}
              header="Notify (SMS)"
              style={{ width: "200px" }}
            />
            <Column
              className={styles.hoverableCell}
              body={(rowData) => {
                return (
                  <>
                    {rowData.active ? (
                      <Button
                        icon="pi pi-eye"
                        className={`p-button-text ${styles.activeBtn}`}
                        disabled={rowData.isDeleted === 1}
                        onClick={() => {
                          updateStation({
                            id: rowData.id,
                            subdomain: getSubdomain(),
                            data: {
                              active: !rowData.active,
                            },
                          });
                        }}
                      />
                    ) : (
                      <Button
                        icon="pi pi-eye-slash"
                        className={`p-button-text ${styles.activeBtn}`}
                        disabled={rowData.isDeleted === 1}
                        onClick={() => {
                          updateStation({
                            id: rowData.id,
                            subdomain: getSubdomain(),
                            data: {
                              active: !rowData.active,
                            },
                          });
                        }}
                      />
                    )}
                  </>
                );
              }}
              style={{ width: "96px" }}
              header="Active"
            />
            <Column
              className={styles.hoverableCell}
              body={actionBodyTemplate}
              header="Actions"
              style={{ width: "242px" }}
            />
          </DataTable>
        ))}

      {/* Delete modal */}
      <Dialog
        closable={false}
        blockScroll
        header={
          <div style={{ width: "100%", textAlign: "center" }}>
            Are You Sure?
          </div>
        }
        visible={showDeleteModal}
        onHide={() => {
          setShowDeleteModal(false);
        }}
        footer={() => (
          <div className="flex flex-column gap-2 justify-content-end">
            <Button
              label="Delete"
              className={`p-button-success ${styles.deleteDialogBtn}`}
              onClick={
                deleteMode === "permanently" ? onPermanentlyDelete : onDelete
              }
            />
            <Button
              label="Cancel"
              text
              className={`p-button-success ${styles.cancelDialogBtn}`}
              onClick={() => {
                setShowDeleteModal(false);
              }}
            />
          </div>
        )}
        className={`p-fluid ${styles.deleteModal}`}
      >
        <div className={styles.text}>
          <span>
            You want to {deleteMode === "permanently" && "permanently"} delete
            the food station <strong>{name}</strong>.
          </span>
          Confirm this action by pressing ‘Delete’ or cancel it.
        </div>
      </Dialog>

      {/* Image modal */}
      <Dialog
        header={`Food Station Image`}
        visible={showImageModal}
        blockScroll
        onHide={() => {
          setShowImageModal(false);
        }}
        footer={() => (
          <div className="flex gap-2 justify-content-end">
            <FileUpload
              mode="basic"
              onSelect={onImageSelect}
              customUpload
              uploadHandler={onImageUpload}
              onUpload={() => {}}
              accept="image/*"
              selectedFileLabel="Upload"
              className={styles.uploadBtn}
            />
          </div>
        )}
        className={`p-fluid ${styles.imageModal}`}
      >
        <form>
          <div className={styles.preview}>
            <div className={styles.cardWrapper}>
              <div className={styles.stationCard}>
                <div
                  style={{
                    backgroundImage: `url(${currentImage})`,
                  }}
                  className={styles.image}
                ></div>
                <div className={styles.titleWrapper}>
                  <div>{imageStationName}</div>
                </div>
              </div>
            </div>
          </div>
          <p>Minimum image resolution 1438x996px</p>
        </form>
      </Dialog>

      {/* Sound modal */}
      <Dialog
        header={`Edit New Order Sound`}
        visible={showSoundModal}
        blockScroll
        onHide={() => {
          setShowSoundModal(false);
        }}
        footer={() => (
          <div className="flex gap-2 justify-content-end">
            <FileUpload
              mode="basic"
              onSelect={onSoundSelect}
              customUpload
              uploadHandler={onSoundUpload}
              onUpload={() => {}}
              selectedFileLabel="Upload"
              className={styles.uploadBtn}
            />
          </div>
        )}
        className={`p-fluid ${styles.soundModal}`}
      >
        <form>
          <div className={styles.preview}>
            {currentSound && <audio controls src={currentSound}></audio>}
          </div>
        </form>
      </Dialog>

      {/* Import Modal */}
      <ImportModal
        uploadRef={uploadRef}
        showImportModal={showImportModal}
        setShowImportModal={setShowImportModal}
        onFileUpload={onFileUpload}
      />
    </div>
  );
};

export default Stations;
